import { DialogComponent } from '@theme/dialog';

/**
 * fc-popup — an informational popup that opens itself on visit.
 *
 * Extends Horizon's DialogComponent so the whole modal engine is native/reused (native
 * <dialog> + showModal focus-trap, Esc to close, click-outside to close, scroll-lock,
 * open/close animations). The only thing added here is the auto-open behaviour:
 *   • open after a configurable delay,
 *   • once per browsing session (sessionStorage), so a returning visitor isn't nagged.
 *
 * Fail-safe: no JS → the <dialog> is simply never opened (it stays a closed, invisible
 * dialog). Reduced-motion is honoured by the CSS (animations gated to no-preference); the
 * close path resolves instantly when no animation runs, so scroll-lock can never get stuck.
 */
class FcPopup extends DialogComponent {
  /** @type {number | null} */
  #timer = null;

  connectedCallback() {
    super.connectedCallback();

    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const delay = Number(this.getAttribute('data-delay')) || 0;
    const key = `fc-popup:${this.getAttribute('data-popup-key') || 'default'}`;

    if (designMode) {
      // In the theme editor: ignore the session gate + reopen when the section is selected,
      // so Filippo can always see it while editing.
      this.#scheduleOpen(delay, null);
      document.addEventListener('shopify:section:select', this.#onSectionSelect);
      return;
    }

    try {
      if (sessionStorage.getItem(key) === '1') return;
    } catch (e) {
      // sessionStorage unavailable (private mode / blocked) → fall through and just show it.
    }

    this.#scheduleOpen(delay, key);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) clearTimeout(this.#timer);
    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
  }

  /**
   * @param {number} delay - milliseconds before opening
   * @param {string | null} key - sessionStorage key to mark as shown (null = don't record)
   */
  #scheduleOpen(delay, key) {
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      this.showDialog();
      if (key) {
        try {
          sessionStorage.setItem(key, '1');
        } catch (e) {
          // ignore
        }
      }
    }, delay);
  }

  #onSectionSelect = (event) => {
    if (event.detail && event.detail.sectionId === this.getAttribute('data-section-id')) {
      this.showDialog();
    }
  };
}

if (!customElements.get('fc-popup')) customElements.define('fc-popup', FcPopup);
